import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Grid,
  Typography,
  TextField,
  Button,
  Switch,
  FormHelperText,
  MenuItem,
  Fab,
  Icon
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Formik, FieldArray, Form } from 'formik';
import * as yup from 'yup';
import { saveEarningRulesApi } from 'api';


const emptyRuleAction = {
  attribute: 'sku',
  operator: '==',
  value: ''
};

const defaultCustomerGroups = [
  {
    id: 0,
    code: 'Not Logged In'
  }
];
const earningPercentValues = [1, 2, 4, 5, 10];

const RewardEarnRule = ({rule, products = [], brands = [], categories = [], setOpenDialogRewardPage}) => {
  const dispatch = useDispatch();
  const {customerGroups = []} = useSelector((state) => state.customers);
  const allCustomerGroups = [...defaultCustomerGroups, ...customerGroups];

  const handleSaveEarningRule = async (values) => {
    const updatedRule = {...rule, ...values};
    const isSuccess = await saveEarningRulesApi({dispatch, rule: updatedRule});

    if (isSuccess) {
      setOpenDialogRewardPage(true);
    }
  };

  return (
    <Formik
      initialValues={{
        isActive: rule.active,
        name: rule.name,
        description: rule.description,
        money_step: rule.money_step,
        point_amount: rule.point_amount,
        actions: rule.actions,
        aggregator: rule.aggregator,
        aggregator_value: rule.aggregator_value,
        customer_group_ids: rule.customer_group_ids || [],
      }}
      validationSchema={
        yup.object({
          name: yup
            .string(),
          description: yup
            .string(),
          customer_group_ids: yup
            .array()
            .min(1, 'Choose a customer group')
            .required('Field is required'),
          money_step: yup
            .number(),
          actions: yup
            .array()
            .of(
              yup.object().shape({
                attribute: yup
                  .string()
                  .required('Field is required'),
                operator: yup
                  .string()
                  .required('Field is required'),
                value: yup.lazy((val) => {
                  return (Array.isArray(val)
                    ? yup.array('Field is required').min(1, 'Field is required').required('Field is required')
                    : yup.string('Field is required').typeError('Field is required').required('Field is required'));
                }
                )
              }))
        })
      }
      onSubmit={handleSaveEarningRule}
    >
      {({
        values,
        errors,
        touched,
        handleBlur,
        handleChange,
        handleSubmit,
        setFieldValue
      }) => {
        return (
          <Form onSubmit={handleSubmit} noValidate>
            <Typography className="font-bold my-8">{rule.name}</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4} md={3}>Enable:</Grid>
              <Grid item xs={12} sm={8} md={9}>
                <Switch
                  color="primary"
                  checked={values.isActive}
                  onChange={(event) => setFieldValue('isActive', event.target.checked)}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>Rule Name:</Grid>
              <Grid item xs={12} sm={8} md={9}>
                <TextField
                  fullWidth
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText="Specified Earning point rule name"
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>Description:</Grid>
              <Grid item xs={12} sm={8} md={9}>
                <TextField
                  fullWidth
                  multiline
                  name="description"
                  value={values.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText="Rule Description will be shown on your store for store customers"
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                Customer Groups <span className="text-error">*</span>
              </Grid>
              <Grid item xs={12} sm={8} md={9}>
                <Autocomplete
                  multiple
                  fullWidth
                  disableClearable
                  size="small"
                  className="max-w-600"
                  name="customer_group_ids"
                  options={allCustomerGroups}
                  value={allCustomerGroups.filter((g) => values.customer_group_ids?.includes(g.id))}
                  getOptionLabel={(option) => option.code}
                  onChange={(_, value, action, option) => {
                    const selectedOptionId = option?.option?.id;

                    if (action === 'select-option') {
                      setFieldValue(
                        'customer_group_ids',
                        value !== null ?
                          [...values.customer_group_ids, selectedOptionId]
                          : values.customer_group_ids
                      );
                    }

                    if (action === 'remove-option') {
                      setFieldValue(
                        'customer_group_ids',
                        value !== null ?
                          values.customer_group_ids.filter((id) => id !== selectedOptionId)
                          : values.customer_group_ids
                      );
                    }
                  }}
                  onBlur={(e, value, action, option) => {
                    const selectedOptionId = option?.option?.id;

                    if (action === 'select-option') {
                      setFieldValue(
                        'customer_group_ids',
                        value !== null ?
                          [...values.customer_group_ids, selectedOptionId]
                          : values.customer_group_ids
                      );
                    }

                    if (action === 'remove-option') {
                      setFieldValue(
                        'customer_group_ids',
                        value !== null ?
                          values.customer_group_ids.filter((id) => id !== selectedOptionId)
                          : values.customer_group_ids
                      );
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      variant="outlined"
                      label="Customer Groups"
                      placeholder="Choose customer group"
                      error={touched.customer_group_ids && Boolean(errors.customer_group_ids)}
                      helperText={touched.customer_group_ids && errors.customer_group_ids}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>Percent of Earning:</Grid>
              <Grid item xs={12} sm={8} md={9}>
                <div className="flex items-center">
                  <TextField
                    select
                    name="percent_of_earning"
                    value={100 / values.money_step}
                    className="mr-2 min-w-100 text-center"
                    onChange={(e) => {
                      const {value} = e.target;
                      handleChange(e);
                      setFieldValue('point_amount', 1);
                      setFieldValue('money_step', 100 / value);
                    }}
                    onBlur={(e) => {
                      const {value} = e.target;
                      handleBlur(e);
                      setFieldValue('point_amount', 1);
                      setFieldValue('money_step', 100 / value);
                    }}
                  >
                    {earningPercentValues.map((value, index) => (
                      <MenuItem key={index} value={value}>{value}</MenuItem>
                    ))}
                  </TextField>{' '}%
                </div>
                <FormHelperText>Don&apos;t forget to change rule name and description to inform your customers</FormHelperText>
              </Grid>
              <Grid item xs={12} sm={4} md={3}>Allow Earn Points if:</Grid>
              <Grid item xs={12} sm={8} md={9}>
                <FieldArray name="actions">
                  {({ push, remove }) => {
                    return (
                      <>
                        {!values.actions.length ?
                          <>
                            <Fab
                              color="primary"
                              size="small"
                              className="m-4"
                              onClick={() => push(emptyRuleAction)}
                              title="Add new rule action"
                            >
                              <Icon>add</Icon>
                            </Fab>
                          </>
                          :
                          <>
                            <Typography variant="body1" className="text-16 text-muted mb-4">
                              If{' '}
                              <TextField
                                select
                                name="aggregator"
                                value={values.aggregator}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                inputProps={{ className: 'py-1 mx-3' }}
                              >
                                <MenuItem value="all">ALL</MenuItem>
                                <MenuItem value="any">ANY</MenuItem>
                              </TextField>{' '}
                              of next conditions are{' '}
                              <TextField
                                select
                                name="aggregator_value"
                                value={values.aggregator_value}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                inputProps={{ className: 'py-1 mx-3' }}
                              >
                                <MenuItem value="1">TRUE</MenuItem>
                                <MenuItem value="0">FALSE</MenuItem>
                              </TextField>{' '}
                            </Typography>
                            {values.actions.map((_, idx) => {
                              return (
                                <Grid  item xs={12} key={idx}>
                                  <Grid container spacing={3}>
                                    <Grid item xs={12} sm={4} md={3}>
                                      <TextField
                                        fullWidth
                                        select
                                        label="Select Type"
                                        name={`actions[${idx}].attribute`}
                                        value={values.actions[idx].attribute}
                                        onChange={(event) => {
                                          setFieldValue(`actions[${idx}].value`, '');
                                          setFieldValue(`actions[${idx}].attribute`, event.target.value);
                                        }}
                                        onBlur={handleBlur}
                                        error={touched.actions?.length &&
                                          errors.actions?.length &&
                                          touched.actions[idx]?.attribute &&
                                          Boolean(errors.actions[idx]?.attribute)
                                        }
                                        helperText={touched.actions?.length &&
                                            errors.actions?.length &&
                                            touched.actions[idx]?.attribute &&
                                            errors.actions[idx]?.attribute
                                        }
                                      >
                                        <MenuItem value="sku">SKU</MenuItem>
                                        <MenuItem value="brand">Brand</MenuItem>
                                        <MenuItem value="category_ids">Category</MenuItem>
                                      </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={4} md={3}>
                                      {values.actions[idx].attribute === 'sku' &&
                                        <TextField
                                          fullWidth
                                          select
                                          label="Operator"
                                          name={`actions[${idx}].operator`}
                                          value={values.actions[idx].operator}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={touched.actions?.length &&
                                            errors.actions?.length &&
                                            touched.actions[idx]?.operator &&
                                            Boolean(errors.actions[idx]?.operator)
                                          }
                                          helperText={touched.actions?.length &&
                                              errors.actions?.length &&
                                              touched.actions[idx]?.operator &&
                                              errors.actions[idx]?.operator
                                          }
                                        >
                                          <MenuItem value="==">is</MenuItem>
                                          <MenuItem value="!=">is not</MenuItem>
                                          <MenuItem value="{}">contain</MenuItem>
                                          <MenuItem value="!{}">does not contain</MenuItem>
                                        </TextField>
                                      }
                                      {values.actions[idx].attribute !== 'sku' &&
                                        <TextField
                                          fullWidth
                                          select
                                          label="Operator"
                                          name={`actions[${idx}].operator`}
                                          value={values.actions[idx].operator}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={touched.actions?.length &&
                                            errors.actions?.length &&
                                            touched.actions[idx]?.operator &&
                                            Boolean(errors.actions[idx]?.operator)
                                          }
                                          helperText={touched.actions?.length &&
                                              errors.actions?.length &&
                                              touched.actions[idx]?.operator &&
                                              errors.actions[idx]?.operator
                                          }
                                        >
                                          <MenuItem value="==">is</MenuItem>
                                          <MenuItem value="!=">is not</MenuItem>
                                        </TextField>
                                      }
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={5}>
                                      {values.actions[idx].attribute === 'sku' &&
                                        <TextField
                                          fullWidth
                                          select
                                          label="Value"
                                          name={`actions[${idx}].value`}
                                          value={values.actions[idx].value}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={touched.actions?.length &&
                                            errors.actions?.length &&
                                            touched.actions[idx]?.value &&
                                            Boolean(errors.actions[idx]?.value)
                                          }
                                          helperText={touched.actions?.length &&
                                              errors.actions?.length &&
                                              touched.actions[idx]?.value &&
                                              errors.actions[idx]?.value
                                          }
                                        >
                                          <MenuItem value="PROD">PROD</MenuItem>
                                          <MenuItem value="TREAT">TREAT</MenuItem>
                                          <MenuItem value="GIFT">GIFT</MenuItem>
                                          <MenuItem value="MEMBER">MEMBER</MenuItem>
                                          <MenuItem value="EVENT">EVENT</MenuItem>
                                          {products.map((product) => (
                                            <MenuItem value={`${product.sku}`} key={`${product.sku}`}>
                                              {`${product.sku} (${product.name})`}
                                            </MenuItem>
                                          ))
                                          }
                                        </TextField>
                                      }
                                      {values.actions[idx].attribute === 'brand' &&
                                        <TextField
                                          fullWidth
                                          select
                                          label="Value"
                                          value={values.actions[idx].value}
                                          name={`actions[${idx}].value`}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={touched.actions?.length &&
                                            errors.actions?.length &&
                                            touched.actions[idx]?.value &&
                                            Boolean(errors.actions[idx]?.value)
                                          }
                                          helperText={touched.actions?.length &&
                                              errors.actions?.length &&
                                              touched.actions[idx]?.value &&
                                              errors.actions[idx]?.value
                                          }
                                        >
                                          {brands.map((brand) => (
                                            <MenuItem value={`${brand.option_id}`} key={`${brand.option_id}`}>
                                              {brand.value}
                                            </MenuItem>
                                          ))
                                          }
                                        </TextField>
                                      }
                                      {values.actions[idx].attribute === 'category_ids' &&
                                        <TextField
                                          fullWidth
                                          select
                                          label="Value"
                                          value={values.actions[idx].value}
                                          name={`actions[${idx}].value`}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={touched.actions?.length &&
                                            errors.actions?.length &&
                                            touched.actions[idx]?.value &&
                                            Boolean(errors.actions[idx]?.value)
                                          }
                                          helperText={touched.actions?.length &&
                                              errors.actions?.length &&
                                              touched.actions[idx]?.value &&
                                              errors.actions[idx]?.value
                                          }
                                        >
                                          {categories.map((category) => (
                                            <MenuItem value={`${category.id}`} key={`${category.id}`}>
                                              {category.nameWithPath}
                                            </MenuItem>
                                          ))
                                          }
                                        </TextField>
                                      }
                                    </Grid>
                                    <Grid item xs={12} sm={2} md={1}>
                                      <Fab
                                        size="small"
                                        className="m-2"
                                        onClick={() => remove(idx)}
                                        title="Remove rule action"
                                      >
                                        <Icon>delete</Icon>
                                      </Fab>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              );
                            })}
                            <Fab
                              color="primary"
                              size="small"
                              className="m-2"
                              onClick={() => push(emptyRuleAction)}
                              title="Add new rule action"
                            >
                              <Icon>add</Icon>
                            </Fab>
                          </>
                        }
                      </>
                    );
                  }}
                </FieldArray>
              </Grid>
            </Grid>
            <div className="flex justify-end">
              <Button variant="contained" color="primary" className="mt-10 min-w-200" type="submit">Save Rule</Button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default RewardEarnRule;
